import { useRef, useState } from "react"
import Form, { useForm } from "../../gui/Form/Form"
import FormControl from "../../gui/Form/FormControl"
import Validators from "../../gui/Form/Validators"
import { useOverlay } from "../../gui/Overlay/OverlayProvider"
import { usePath } from "../../libs/path/PathProvider"
import AccountSelect from "../widgets/AccountSelect"
import Button from "../../gui/Button/Button"
import TextInput, { Formatters, ITextInput } from "../../gui/TextInput/TextInput"
import DatePicker, { IDatePicker } from "../../gui/Calendar/DatePicker"
import TransTypeSelect from "../widgets/TransTypeSelect"
import Checkbox, { IToggleButton } from "../../gui/Button/Checkbox"
import { IDropdown } from "../../gui/Dropdown/Dropdown"
import usePortfolio from "../../hooks/PortfolioProvider"
import { useSnackbar } from "../../gui/Snackbar/SnackbarProvider"
import { ETransactionType } from "../../shared/ITransaction"
import PortUtils from "../../portlibs/PortUtils"
import { isCashEq } from "../../shared/Consts"
import createTrans, { createTransactionRow } from "../../models/TransFactory"
import PortAccount from "../../models/PortAccount"

export default function ReinvestDlg(props:{holdingId:string}){
    const {query} = usePath()
    const [busy,setBusy] = useState<boolean>(false)
    const refAction = useRef<IDropdown<ETransactionType>|null>(null)
    const refAmount = useRef<ITextInput|null>(null)
    const refQty = useRef<ITextInput|null>(null)
    const refAccount = useRef<IDropdown<number>|null>(null)
    const refDate = useRef<IDatePicker|null>(null)
    const refNote = useRef<ITextInput|null>(null)
    const refUpdateCash = useRef<IToggleButton|null>(null)
    const refPrice = useRef<ITextInput|null>(null)

    const {popOverlay} = useOverlay()
    const {valid} = useForm()
    const {addSnackbar} = useSnackbar()
    const {portfolio} = usePortfolio()
    const {account} = PortUtils.getFilter(query, 0)
    const holding = portfolio.holding(props.holdingId)!.filterByAccount(account)

    const amount = holding.payout
    const qty = holding.price.price ? ~~(amount/holding.price.price) : 0
    
    const onSave = async () => {
        if (!refQty.current || !refDate.current || !refNote.current || !refAction.current || busy) return
        else setBusy(false)

        try {
            var note = refNote.current!.value ? refNote.current.value + " dividend" : `${holding.id} dividend`
            const divTrans = createTrans(holding.id, holding, createTransactionRow(refAction.current!.value!, refAccount.current!.value!, holding.id, 1, Number(refAmount.current!.value!), note))
            await PortAccount.i.update(portfolio, divTrans, refUpdateCash.current!.checked)

            var note = refNote.current!.value ? refNote.current.value + " reinvest" : `${holding.id} reinvest`
            const reinvest = createTrans(holding.id, holding, createTransactionRow(ETransactionType.reinvest, refAccount.current!.value!, holding.id, Number(refPrice.current!.value!), Number(refQty.current!.value!), note))
            await PortAccount.i.update(portfolio, reinvest, refUpdateCash.current!.checked)            
            popOverlay(PortUtils.AddHoldingId)
        }catch(e){
            addSnackbar(e)
        }finally{
            setBusy(false)
        }
    }

    return <>
        <h2 className="title">{holding.name}</h2>
        <div className="content">
            <div className="grid gap-16">
                <AccountSelect ref={refAccount} defaultValue={account ?? 0}/>
                <TransTypeSelect ref={refAction} filter="dividend" defaultValue={isCashEq(holding.id) ? ETransactionType.interest : ETransactionType.dividend}/>
                <Checkbox ref={refUpdateCash}>Cash</Checkbox>
            </div>
            <div className="grid gap-16">
                <FormControl validators={[Validators.NonZeroNumber("Amount")]}>
                    <TextInput ref={refAmount} placeholder="Amount" float={true} evaluator={Formatters.Evaluate} defaultValue={amount.toString()}/>
                </FormControl>
                <FormControl validators={[Validators.Number("Quantity")]}>
                    <TextInput ref={refQty} placeholder="Quantity" float={true} evaluator={Formatters.Evaluate} defaultValue={qty.toString()}/>
                </FormControl>
                <FormControl validators={[Validators.Number("Price")]}>
                    <TextInput ref={refPrice} placeholder="Price" float={true} evaluator={Formatters.Evaluate} defaultValue={holding.price.price.toString()}/>
                </FormControl>
                <FormControl validators={[Validators.Required("Date")]}>
                    <DatePicker ref={refDate} placeholder="Date" float={true} defaultValue={new Date()}/>
                </FormControl>
            </div>
            <TextInput ref={refNote} placeholder="Notes" float={true}/>
        </div>
        <div className="actions">
            <Button onClick={_ => popOverlay(PortUtils.AddHoldingId)}>Cancel</Button>
            <Button variant="contained" onClick={onSave} disabled={!valid||busy}>Save</Button>
        </div>
    </>
}

export function useReinvestDlg(){
    const {addOverlay,hasOverlay} = useOverlay()
    const {hash,setHash} = usePath()
    const {portfolio} = usePortfolio()

    const afterClosed = () => {
        const {holdingId} = PortUtils.parseHash(hash)
        setHash(PortUtils.createHash(holdingId))
    }
    const addReinvest = (holdingId:string) => {
        if (hasOverlay(PortUtils.AddHoldingId) || !portfolio.holding(holdingId)) return
        addOverlay(PortUtils.AddHoldingId, {
            children:<Form><ReinvestDlg holdingId={holdingId}/></Form>,
            className:"dialog",
            afterClosed
        })
    }
    return {addReinvest}
}